import React from 'react';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, CircularProgress, InputAdornment, Menu, TextField } from '@mui/material';
import hourGlassIcon from 'assets/images/hourGlass.svg';
import sendIcon from 'assets/images/SendIcon.svg';
import { CustomCheckbox } from 'components/CustomCheckbox';
import { MissingDataCounts, MissingDataSelection, NotMatchingVarianceFilter } from '../utils/filters';

interface StoresFilterProps {
  totalCount: number;
  matchingCount: number;
  surplusCount: number;
  shortCount: number;
  shortDefault: number;
  surplusDefault: number;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  isAllActive: boolean;
  onResetAllFilters: () => void;
  isMatchingActive: boolean;
  onToggleMatching: () => void;
  isNotMatchingActive: boolean;
  onToggleNotMatching: () => void;
  onShowSurplusNotMatching: () => void;
  onShowShortNotMatching: () => void;
  onApplyNotMatching: (filter: NotMatchingVarianceFilter) => void;
  missingDataSelection: MissingDataSelection;
  onToggleMissingData: () => void;
  onChangeMissingDataSelection: (selection: MissingDataSelection) => void;
  missingDataCounts: MissingDataCounts;
  isLoading: boolean;
}

const StoresFilter: React.FC<StoresFilterProps> = ({
  totalCount,
  matchingCount,
  surplusCount,
  shortCount,
  shortDefault,
  surplusDefault,
  searchText,
  onSearchTextChange,
  isAllActive,
  onResetAllFilters,
  isMatchingActive,
  onToggleMatching,
  isNotMatchingActive,
  onToggleNotMatching,
  onShowSurplusNotMatching,
  onShowShortNotMatching,
  onApplyNotMatching,
  missingDataSelection,
  onToggleMissingData,
  onChangeMissingDataSelection,
  missingDataCounts,
  isLoading
}) => {
  const [notMatchingAnchorEl, setNotMatchingAnchorEl] = React.useState<null | HTMLElement>(null);
  const [missingDataAnchorEl, setMissingDataAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isNotMatchingOpen, setIsNotMatchingOpen] = React.useState<boolean>(false);
  const [isMissingDataOpen, setIsMissingDataOpen] = React.useState<boolean>(false);
  const [notMatchingMenuWidth, setNotMatchingMenuWidth] = React.useState<number | undefined>(undefined);
  const [missingDataMenuWidth, setMissingDataMenuWidth] = React.useState<number | undefined>(undefined);
  const [surplusValue, setSurplusValue] = React.useState<string>('');
  const [shortValue, setShortValue] = React.useState<string>('');
  const missingDataFilterClassName = [
    'stores-filter-bar__filter-pill',
    'stores-filter-bar__filter-pill--missing-data',
    missingDataSelection.any || missingDataSelection.lotto || missingDataSelection.pos
      ? 'stores-filter-bar__filter-pill--active'
      : '',
  ]
    .filter(Boolean)
    .join(' ');
  const isMissingAuditsActive =
    missingDataSelection.audits &&
    !missingDataSelection.any &&
    !missingDataSelection.lotto &&
    !missingDataSelection.pos;

  React.useEffect(() => {
    if (!isNotMatchingActive) {
      setShortValue('');
      setSurplusValue('');
    }
  }, [isNotMatchingActive]);

  const sanitizeNumericText = (value: string): string => value.replace(/[^0-9.]/g, '');
  const parseFilterValue = (value: string): number | undefined => {
    if (!value) return undefined;
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return undefined;
    return parsed;
  };

  const handleApplyNotMatching = () => {
    const parsedShort = parseFilterValue(shortValue);
    const parsedSurplus = parseFilterValue(surplusValue);
    const isBothZero = parsedShort === 0 && parsedSurplus === 0;

    onApplyNotMatching({
      short: isBothZero ? undefined : parsedShort,
      surplus: isBothZero ? undefined : parsedSurplus,
    });
    setIsNotMatchingOpen(false);
  };

  const handleOpenNotMatchingMenu = (event: React.MouseEvent<HTMLElement>) => {
    const filterPill = event.currentTarget.closest('.stores-filter-bar__filter-pill');
    setNotMatchingAnchorEl(event.currentTarget);
    setNotMatchingMenuWidth(filterPill instanceof HTMLElement ? filterPill.clientWidth : event.currentTarget.clientWidth);
    setIsNotMatchingOpen(true);
  };

  const handleCloseNotMatchingMenu = () => {
    setIsNotMatchingOpen(false);
  };

  const handleOpenMissingDataMenu = (event: React.MouseEvent<HTMLElement>) => {
    const filterPill = event.currentTarget.closest('.stores-filter-bar__filter-pill');
    setMissingDataAnchorEl(event.currentTarget);
    setMissingDataMenuWidth(filterPill instanceof HTMLElement ? filterPill.clientWidth : event.currentTarget.clientWidth);
    setIsMissingDataOpen(true);
  };

  const handleCloseMissingDataMenu = () => {
    setIsMissingDataOpen(false);
  };

  const formatCurrencyRangeValue = (value: number): string =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);

  const toggleMissingData = (key: 'any' | 'lotto' | 'pos' | 'audits') => {
    const prev = missingDataSelection;

    if (key === 'any') {
      const nextAny = !prev.any;
      onChangeMissingDataSelection({ any: nextAny, lotto: nextAny, pos: nextAny, audits: nextAny });
      return;
    }

    const next = { ...prev, [key]: !prev[key] };
    next.any = next.lotto && next.pos && next.audits;
    onChangeMissingDataSelection(next);
  };

  const handleToggleMissingAudits = () => {
    onChangeMissingDataSelection({
      any: false,
      lotto: false,
      pos: false,
      audits: !isMissingAuditsActive,
    });
  };

  return (
    <div className="stores-filter-bar">
      <div className="stores-filter-bar__left">
        <Box
          component="button"
          type="button"
          className={`stores-filter-bar__filter-pill stores-filter-bar__filter-pill--all ${isAllActive ? 'stores-filter-bar__filter-pill--active' : ''}`}
          onClick={onResetAllFilters}
        >
          <span className="stores-filter-bar__label stores-filter-bar__all-label">All Stores</span>
          {isLoading ? (
            <span className="stores-filter-bar__count stores-filter-bar__count--all stores-filter-bar__count--loading">
              <CircularProgress size={14} sx={{ color: '#2C6CA0' }} />
            </span>
          ) : (
            <span className="stores-filter-bar__count stores-filter-bar__count--all stores-filter-bar__count-text">{totalCount}</span>
          )}
        </Box>

        <Box
          component="button"
          type="button"
          className={`stores-filter-bar__filter-pill ${isMatchingActive ? 'stores-filter-bar__filter-pill--active' : ''}`}
          onClick={onToggleMatching}
        >
          <span className="stores-filter-bar__label">Matching Stores</span>
          {isLoading ? (
            <span className="stores-filter-bar__count stores-filter-bar__count--matching stores-filter-bar__count--loading">
              <CircularProgress size={12} sx={{ color: '#05BE43' }} />
            </span>
          ) : (
            <span className="stores-filter-bar__count stores-filter-bar__count--matching stores-filter-bar__count-text">{matchingCount}</span>
          )}
        </Box>

        <Box
          component="div"
          className={`stores-filter-bar__filter-pill stores-filter-bar__filter-pill--not-matching ${isNotMatchingActive ? 'stores-filter-bar__filter-pill--active' : ''}`}
        >
          <button type="button" className="stores-filter-bar__not-matching-main" onClick={onToggleNotMatching}>
            <span className="stores-filter-bar__label">Not Matching</span>
          </button>
          <span className="stores-filter-bar__not-matching-values">
            {isLoading ? (
              <span className="stores-filter-bar__not-matching-value stores-filter-bar__not-matching-value--loading">
                <CircularProgress size={12} sx={{ color: '#8f98ad' }} />
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className="stores-filter-bar__not-matching-value stores-filter-bar__not-matching-value--primary stores-filter-bar__count-text"
                  onClick={onShowSurplusNotMatching}
                >
                  <span className="stores-filter-bar__variance-label">Surplus</span>
                  <span>{surplusCount}</span>
                </button>
                <button
                  type="button"
                  className="stores-filter-bar__not-matching-value stores-filter-bar__not-matching-value--secondary stores-filter-bar__count-text"
                  onClick={onShowShortNotMatching}
                >
                  <span className="stores-filter-bar__variance-label">Deficit</span>
                  <span>{shortCount}</span>
                </button>
              </>
            )}
          </span>
          <button
            type="button"
            className="stores-filter-bar__not-matching-arrow-button"
            aria-label="Open not matching variance filter"
            onClick={handleOpenNotMatchingMenu}
          >
            <KeyboardArrowDownIcon className="stores-filter-bar__chip-arrow" />
          </button>
        </Box>

        <Box
          component="div"
          className={missingDataFilterClassName}
        >
          <button type="button" className="stores-filter-bar__missing-data-main" onClick={onToggleMissingData}>
            <img src={hourGlassIcon} alt="missing data" className="stores-filter-bar__missing-data-icon" />
            <span className="stores-filter-bar__label">Missing Data</span>
            {isLoading ? (
              <span className="stores-filter-bar__missing-data-count stores-filter-bar__missing-data-count--loading">
                <CircularProgress size={12} sx={{ color: '#FFA600' }} />
              </span>
            ) : (
              <span className="stores-filter-bar__missing-data-count stores-filter-bar__count-text">{missingDataCounts.anyCount}</span>
            )}
          </button>
          <button
            type="button"
            className="stores-filter-bar__missing-data-arrow-button"
            aria-label="Open missing data filter"
            onClick={handleOpenMissingDataMenu}
          >
            <KeyboardArrowDownIcon className="stores-filter-bar__missing-data-arrow" />
          </button>
        </Box>

        <Box
          component="button"
          type="button"
          className={`stores-filter-bar__filter-pill stores-filter-bar__filter-pill--missing-audits ${isMissingAuditsActive ? 'stores-filter-bar__filter-pill--active' : ''}`}
          onClick={handleToggleMissingAudits}
        >
          <span className="stores-filter-bar__label">Missing Audits</span>
          {isLoading ? (
            <span className="stores-filter-bar__count stores-filter-bar__count--loading">
              <CircularProgress size={12} sx={{ color: '#FFA600' }} />
            </span>
          ) : (
            <span className="stores-filter-bar__missing-audits-count stores-filter-bar__count-text">
              {missingDataCounts.auditsCount}
            </span>
          )}
        </Box>
      </div>

      <div className="stores-filter-bar__search">
        <TextField
          fullWidth
          size="small"
          placeholder="Search stores..."
          value={searchText}
          onChange={(event) => onSearchTextChange(event.target.value)}
          className="stores-filter-bar__search-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="stores-filter-bar__search-icon" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <Menu
        anchorEl={notMatchingAnchorEl}
        open={isNotMatchingOpen}
        onClose={handleCloseNotMatchingMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          className: 'stores-filter-menu',
          style: { width: notMatchingMenuWidth ? `${notMatchingMenuWidth}px` : undefined },
        }}
        MenuListProps={{
          disablePadding: true,
        }}
        TransitionProps={{
          onExited: () => setNotMatchingAnchorEl(null),
        }}
      >
        <div className="stores-filter-menu__content">
          <div className="stores-filter-menu__header">
            <span className="stores-filter-menu__header-title">
              Variance
              <span className="stores-filter-menu__range">
                <span className="stores-filter-menu__range-value stores-filter-menu__range-value--short">
                  -{formatCurrencyRangeValue(shortDefault)}
                </span>
                <span className="stores-filter-menu__range-separator">to</span>
                <span className="stores-filter-menu__range-value stores-filter-menu__range-value--surplus">
                  {formatCurrencyRangeValue(surplusDefault)}
                </span>
              </span>
            </span>
            <Box component="button" type="button" className="stores-filter-menu__header-action" onClick={handleApplyNotMatching}>
              <img src={sendIcon} alt="apply variance" className="stores-filter-menu__header-action-icon" />
            </Box>
          </div>
          <div className="stores-filter-menu__field">
            <div className="stores-filter-menu__label-group">
              <span className="stores-filter-menu__label stores-filter-menu__label--short">Short</span>
              <span className="stores-filter-menu__label-max">
                (<span className="stores-filter-menu__label-max-text">Max:</span>{' '}
                <span className="stores-filter-menu__label-max-value">-{formatCurrencyRangeValue(shortDefault)}</span>)
              </span>
            </div>
            <TextField
              size="small"
              type="text"
              value={shortValue ? (Number(shortValue) === 0 ? '0' : `-${shortValue}`) : ''}
              onChange={(event) => setShortValue(sanitizeNumericText(event.target.value))}
              className="stores-filter-menu__input"
            />
          </div>
          <div className="stores-filter-menu__field">
            <div className="stores-filter-menu__label-group">
              <span className="stores-filter-menu__label stores-filter-menu__label--surplus">Surplus</span>
              <span className="stores-filter-menu__label-max">
                (<span className="stores-filter-menu__label-max-text">Max:</span>{' '}
                <span className="stores-filter-menu__label-max-value">{formatCurrencyRangeValue(surplusDefault)}</span>)
              </span>
            </div>
            <TextField
              size="small"
              type="text"
              value={surplusValue}
              onChange={(event) => setSurplusValue(sanitizeNumericText(event.target.value))}
              className="stores-filter-menu__input"
            />
          </div>
        </div>
      </Menu>

      <Menu
        anchorEl={missingDataAnchorEl}
        open={isMissingDataOpen}
        onClose={handleCloseMissingDataMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          className: 'stores-filter-menu',
          style: { width: missingDataMenuWidth ? `${missingDataMenuWidth}px` : undefined },
        }}
        TransitionProps={{
          onExited: () => setMissingDataAnchorEl(null),
        }}
      >
        <div className="stores-filter-menu__content stores-filter-menu__content--missing-data">
          <Box component="button" type="button" className="stores-missing-data-menu__row" onClick={() => toggleMissingData('any')}>
            <div className="stores-missing-data-menu__left">
              <CustomCheckbox checked={missingDataSelection.any} onChange={() => { }} />
              <span className="stores-missing-data-menu__label">ANY</span>
            </div>
            {isLoading ? <CircularProgress size={16} sx={{ color: '#FFA600' }} /> : <span className="stores-missing-data-menu__count stores-filter-bar__count-text">{missingDataCounts.anyCount}</span>}
          </Box>

          <Box component="button" type="button" className="stores-missing-data-menu__row" onClick={() => toggleMissingData('lotto')}>
            <div className="stores-missing-data-menu__left">
              <CustomCheckbox checked={missingDataSelection.lotto} onChange={() => { }} />
              <span className="stores-missing-data-menu__label">LOTTO</span>
            </div>
            {isLoading ? <CircularProgress size={16} sx={{ color: '#FFA600' }} /> : <span className="stores-missing-data-menu__count stores-filter-bar__count-text">{missingDataCounts.lottoCount}</span>}
          </Box>

          <Box component="button" type="button" className="stores-missing-data-menu__row" onClick={() => toggleMissingData('pos')}>
            <div className="stores-missing-data-menu__left">
              <CustomCheckbox checked={missingDataSelection.pos} onChange={() => { }} />
              <span className="stores-missing-data-menu__label">POS</span>
            </div>
            {isLoading ? <CircularProgress size={16} sx={{ color: '#FFA600' }} /> : <span className="stores-missing-data-menu__count stores-filter-bar__count-text">{missingDataCounts.posCount}</span>}
          </Box>

          <Box component="button" type="button" className="stores-missing-data-menu__row" onClick={() => toggleMissingData('audits')}>
            <div className="stores-missing-data-menu__left">
              <CustomCheckbox checked={missingDataSelection.audits} onChange={() => { }} />
              <span className="stores-missing-data-menu__label">AUDITS</span>
            </div>
            {isLoading ? <CircularProgress size={16} sx={{ color: '#FFA600' }} /> : <span className="stores-missing-data-menu__count stores-filter-bar__count-text">{missingDataCounts.auditsCount}</span>}
          </Box>
        </div>
      </Menu>
    </div>
  );
};

export default StoresFilter;
